import { useEffect, useState } from "react";
import { Menu, Search, Sparkles, X } from "lucide-react";
import ClientLogo from "@/components/shell/client-logo";
import NotificationBell from "@/components/shell/notification-bell";
import OrgSwitcher from "@/components/shell/org-switcher";
import ProfileDropdown from "@/components/shell/profile-dropdown";
import { AiSearchOverlay } from "@/components/shell/ai-search-overlay";
import { Tooltip } from "@/components/ui/tooltip";
import { useAuthStore } from "@/stores/auth-store";
import { cn } from "@/lib/utils";

function isApplePlatform(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || "");
}

const AI_SEARCH_SHORTCUT = isApplePlatform() ? "⌘ K" : "Ctrl K";

/** True when the key event comes from a field the user is typing into. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

function EnvironmentBadge({ mode }: { mode: string }) {
  if (!mode) return null;
  const isProduction = mode === "production" || mode === "live";
  return (
    <span
      data-testid="env-badge"
      className={cn(
        "hidden items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold sm:inline-flex",
        isProduction
          ? "bg-danger/10 text-danger"
          : "bg-info/10 text-info",
      )}
    >
      <span
        aria-hidden
        className={cn("size-1.5 rounded-full", isProduction ? "bg-danger" : "bg-info")}
      />
      {mode.charAt(0).toUpperCase() + mode.slice(1)}
    </span>
  );
}

/**
 * The one header row every authenticated page shares.
 *
 * Below lg it also carries the drawer's menu button, which is why the drawer
 * state is a prop rather than local state: the shell that owns the drawer
 * (`app-shell.tsx`) owns the toggle, and the topbar is the surface that shows
 * it. The prototype (`topbar.tsx` in the design reference) takes the same two
 * props for the same reason — one bar at every width, never a second row
 * stacked above the real header on a phone.
 */
export default function Topbar({
  menuOpen,
  onToggleMenu,
}: {
  menuOpen: boolean;
  onToggleMenu: () => void;
}) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchHover, setSearchHover] = useState(false);
  const experience = useAuthStore((s) => s.experience);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      // Cmd/Ctrl+F is the browser's find-in-page and is never taken over (ALL-5791).
      // AI search lives on Cmd/Ctrl+K, the command-palette convention.
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
        return;
      }
      // "/" focuses the page's own search field, unless the user is already typing.
      if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey && !isTypingTarget(e.target)) {
        const field = document.querySelector<HTMLInputElement>("[data-page-search]");
        if (field) {
          e.preventDefault();
          field.focus();
        }
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <header
      data-testid="topbar"
      className="flex h-14 shrink-0 items-center gap-2 border-b border-line bg-surface px-3 md:h-[68px] md:px-6 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-3.5"
    >
      {/* Left group. `min-w-0` at every level is what lets the application
          switcher's label ellipsise instead of shouldering the search pill
          aside once the row runs out of width (the 1024–1440 overlap). */}
      <div className="flex min-w-0 flex-1 items-center gap-1.5 lg:min-w-0 lg:flex-none">
        <button
          type="button"
          onClick={onToggleMenu}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          className="grid size-9 shrink-0 place-items-center rounded-md border border-line bg-surface text-ink lg:hidden"
        >
          {menuOpen ? <X aria-hidden className="size-4" /> : <Menu aria-hidden className="size-4" />}
        </button>
        <ClientLogo />
      </div>

      <button
        type="button"
        onClick={() => setSearchOpen(true)}
        onMouseEnter={() => setSearchHover(true)}
        onMouseLeave={() => setSearchHover(false)}
        onFocus={() => setSearchHover(true)}
        onBlur={() => setSearchHover(false)}
        aria-label="AI search"
        aria-keyshortcuts={isApplePlatform() ? "Meta+K" : "Control+K"}
        className="relative flex size-9 shrink-0 items-center justify-center rounded-md border border-line bg-surface text-ink outline-none transition-all hover:border-primary/40 md:h-10 md:w-[266px] md:max-w-[90vw] md:shrink md:justify-start md:rounded-full md:border-primary/30 md:bg-primary-soft/40 md:pl-10 md:pr-12 md:text-left md:text-sm md:font-medium md:text-text md:hover:border-primary/60 md:hover:bg-primary-soft/60 md:focus-visible:border-primary md:focus-visible:ring-2 md:focus-visible:ring-primary-soft lg:shrink-0"
      >
        <span aria-hidden className="pointer-events-none text-primary md:absolute md:left-3.5">
          {searchHover ? <Sparkles className="size-4" /> : <Search className="size-4" strokeWidth={1.75} />}
        </span>
        <span className="hidden truncate md:inline">Search…</span>
        <kbd aria-hidden className="pointer-events-none absolute right-3 hidden text-[11px] font-semibold text-primary md:block">{AI_SEARCH_SHORTCUT}</kbd>
      </button>

      <div className="flex shrink-0 items-center justify-end gap-1.5 lg:gap-1">
        <EnvironmentBadge mode={experience.environment.mode} />
        <OrgSwitcher />
        <NotificationBell />

        <div className="ml-0.5 flex items-center gap-2.5 rounded-md py-1 md:ml-1 md:px-2">
          <Tooltip content="Account">
            <ProfileDropdown />
          </Tooltip>
        </div>
      </div>

      <AiSearchOverlay open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
}
